import { STEP_VALIDATION_RULES } from './workflowConstants';

/**
 * Validates if a step has all required fields populated
 * @param {Object} step - The step object to validate
 * @returns {boolean} - True if step is complete, false otherwise
 */
export const isStepComplete = (step) => {
  if (!step.name || !step.type) return false;
  
  const rules = STEP_VALIDATION_RULES[step.type];
  if (!rules) return false;
  
  // Check if all required fields are present and non-empty
  return rules.required.every(field => {
    const value = step.config?.[field];
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined;
  });
};

/**
 * Validates a step configuration and returns validation results
 * @param {Object} step - The step object to validate
 * @returns {Object} - Validation result with isValid flag and errors array
 */
export const validateStep = (step) => {
  const errors = [];
  
  // Basic validation
  if (!step.name || step.name.trim().length === 0) {
    errors.push('Step name is required');
  }
  
  if (!step.type) {
    errors.push('Step type is required');
  }
  
  // Type-specific validation
  const rules = STEP_VALIDATION_RULES[step.type];
  if (rules) {
    rules.required.forEach(field => {
      const value = step.config?.[field];
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        errors.push(`${field} is required for ${step.type} steps`);
      }
    });
  }
  
  // Action routing validation
  if (step.actionRouting && typeof step.actionRouting === 'object') {
    Object.entries(step.actionRouting).forEach(([action, target]) => {
      if (!action || action.trim().length === 0) {
        errors.push('Action routing keys cannot be empty');
      }
      if (!target || target.trim().length === 0) {
        errors.push(`Action "${action}" must have a target`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates multiple steps and their interconnections
 * @param {Array} steps - Array of step objects
 * @returns {Object} - Validation result with overall status and step-specific errors
 */
export const validateWorkflowSteps = (steps) => {
  const stepValidations = {};
  const globalErrors = [];
  let hasStartStep = false;
  
  // Validate individual steps
  steps.forEach((step, index) => {
    stepValidations[step.id] = validateStep(step);
    
    if (step.isStart) {
      hasStartStep = true;
    }
  });
  
  // Global validations
  if (!hasStartStep && steps.length > 0) {
    globalErrors.push('Workflow must have at least one start step');
  }
  
  // Check action routing references
  const stepIds = new Set(steps.map(s => s.id));
  steps.forEach(step => {
    if (step.actionRouting) {
      Object.entries(step.actionRouting).forEach(([action, target]) => {
        if (target !== 'end' && !stepIds.has(target)) {
          if (!stepValidations[step.id].errors) {
            stepValidations[step.id].errors = [];
          }
          stepValidations[step.id].errors.push(
            `Action "${action}" references unknown step: ${target}`
          );
          stepValidations[step.id].isValid = false;
        }
      });
    }
  });
  
  const isOverallValid = Object.values(stepValidations).every(v => v.isValid) && 
                        globalErrors.length === 0;
  
  return {
    isValid: isOverallValid,
    globalErrors,
    stepValidations
  };
};

/**
 * Gets validation summary for display purposes
 * @param {Array} steps - Array of step objects
 * @returns {Object} - Summary with counts and status
 */
export const getValidationSummary = (steps) => {
  const validSteps = steps.filter(isStepComplete);
  const invalidSteps = steps.filter(step => !isStepComplete(step));
  
  return {
    total: steps.length,
    valid: validSteps.length,
    invalid: invalidSteps.length,
    allValid: validSteps.length === steps.length,
    readyForSave: validSteps.length > 0
  };
};